import { useId, useState, type InputHTMLAttributes } from 'react';

import styles from './PinField.module.css';

export interface PinFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'value' | 'onChange' | 'type'
> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** How many boxes to draw; a longer pin still fits, it just adds boxes. */
  length?: number;
  invalid?: boolean;
}

/**
 * A one-time pin drawn as separate boxes over a single real input, so paste,
 * autofill and screen readers all see one ordinary field.
 */
export function PinField({
  label,
  value,
  onChange,
  length = 6,
  invalid = false,
  ...rest
}: PinFieldProps) {
  const id = useId();
  const [focused, setFocused] = useState(false);
  const boxes = Math.max(length, value.length + (value.length >= length ? 0 : 1));
  const cursor = Math.min(value.length, boxes - 1);

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={`${styles.boxes} ${invalid ? styles.invalid : ''}`}>
        <input
          {...rest}
          data-1p-ignore
          aria-invalid={invalid}
          className={styles.input}
          id={id}
          onBlur={(event) => {
            setFocused(false);
            rest.onBlur?.(event);
          }}
          onChange={(event) => onChange(event.target.value.replace(/\s+/g, ''))}
          onFocus={(event) => {
            setFocused(true);
            rest.onFocus?.(event);
          }}
          spellCheck={false}
          type="text"
          value={value}
        />
        {Array.from({ length: boxes }, (_, index) => (
          <span
            aria-hidden="true"
            className={[
              styles.box,
              value[index] ? styles.filled : '',
              focused && index === cursor ? styles.active : '',
            ]
              .filter(Boolean)
              .join(' ')}
            key={index}
          >
            {value[index] ?? ''}
          </span>
        ))}
      </div>
    </div>
  );
}
